import * as d3 from 'd3';

import { BaseSwitch, TBaseSwitchOptionIcon } from 'src/components/BaseSwitch';
import {
  EChartEnergyBillsAndNetTradedType,
  TChartEnergyBillsAndNetTradedProps,
} from './ChartEnergyBillsAndNetTraded.types';
import { EChartTooltips, getChartTooltip } from 'src/constants/chartTooltips';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TPriceEnergyAreaBalance, TPriceEnergyAreaBalanceItem } from 'src/typings/simulation.types';

import { BasePills } from 'src/components/BasePills';
import { ChartDataLoadingWrapper } from 'src/components/ChartDataLoadingWrapper';
import { ChartEnergyBillsAndNetTradedConfig } from 'src/constants/chart';
import { ChartWrapper } from 'src/components/ChartWrapper';
import classnames from 'classnames';
import { formatter } from 'src/utils/formatter';
import s from './ChartEnergyBillsAndNetTraded.module.scss';
import { selectSettingsData } from 'src/redux/configuration/configuration.selectors';
import { selectSidebarExpanded } from 'src/redux/application/application.selectors';
import { useSelector } from 'react-redux';

type TCreateTreemapChart = {
  container: SVGSVGElement;
  data: { children: any[] };
  width: number | string;
  height: number;
  scoreboardType: string;
  currency: string;
};

const getValueKey = (dataType, scoreboardValue) => {
  switch (dataType) {
    case 'Bought':
      return scoreboardValue === 'energy' ? 'bought' : 'spent';
    case 'Sold':
      return scoreboardValue === 'energy' ? 'sold' : 'earned';
    case 'Total':
      return scoreboardValue === 'energy' ? 'total_energy' : 'total_cost';
  }
};

function getFormatedChartData(
  data: Partial<TPriceEnergyAreaBalance> | TPriceEnergyAreaBalanceItem | undefined,
  scoreboardType: string,
  scoreboardValue: string,
) {
  const result: {
    children: { title: string; realValue: number; chartValue: number; scoreboardValue: string }[];
  } = {
    children: [],
  };

  if (!data) {
    return result;
  }

  // Change name of External Trades
  data['Grid Market'] = data['External Trades'];
  // @ts-ignore
  delete data['External Trades'];
  // @ts-ignore
  delete data['Accumulated Trades'];
  // @ts-ignore
  delete data['Totals'];

  if (scoreboardValue === 'energy') {
    delete data['Market Fees'];
  }

  const valueKey = getValueKey(scoreboardType, scoreboardValue);
  const isSingleAsset =
    valueKey && typeof (data as TPriceEnergyAreaBalanceItem)[valueKey] !== 'undefined';

  const valuesArr = isSingleAsset
    ? valueKey
      ? [data[valueKey]]
      : []
    : Object.keys(data).map((key) => (data[key] ? data[key][valueKey] : []));
  const lowestTypeValue = valuesArr.reduce((a, b) => Math.min(a, b));
  const highestTypeValue = valuesArr.reduce((a, b) => Math.max(a, b));

  if (isSingleAsset) {
    result.children.push({
      scoreboardValue,
      title: (data as TPriceEnergyAreaBalanceItem).type || '',
      realValue: data[valueKey!],
      chartValue: data[valueKey!] + Math.abs(lowestTypeValue) * 10 + Math.abs(highestTypeValue),
    });
  } else {
    Object.keys(data).forEach((key) => {
      const value = data[key];

      result.children.push({
        scoreboardValue,
        title: key,
        realValue: value[valueKey],
        chartValue: value[valueKey] + Math.abs(lowestTypeValue) * 10 + Math.abs(highestTypeValue),
      });
    });
  }

  return result;
}

function wrapText(selection, currency) {
  selection.each(function () {
    // @ts-ignore
    const node = d3.select(this);

    const rectWidth = +node.attr('data-width');
    let word;
    const words = node.text().split('_').reverse();
    let line: string[] = [];
    let lineNumber = 0;
    let textSplitInArray: string[] = [];
    const x = node.attr('x');
    const y = node.attr('y');
    let tspan = node.text('').append('tspan').attr('x', x).attr('y', y);

    while (words.length > 1) {
      word = words.pop();
      line.push(word);
      tspan.text(line.join(' '));

      const tspanLength = tspan.node()!.getComputedTextLength();

      const offsetRect = 10;
      const rectWidthWithOffset = rectWidth - offsetRect;

      // compare if tspan is bigger than rectWidthWithOffset
      if (tspanLength > rectWidthWithOffset) {
        // calcule the difference
        const diff = tspanLength - rectWidthWithOffset;

        if (diff > 0) {
          const textLength = word.split('').length;

          // Calculate number of characters per line that fit into g box
          const maxCharacteres = Math.round((textLength * rectWidthWithOffset) / tspanLength);

          // chunck function to split the array based in the maximum of characteres
          textSplitInArray = chunk(word, maxCharacteres);

          // the first key add to the current tspan and other add to new tspan
          textSplitInArray.forEach((e, key) => {
            if (key === 0) {
              tspan.text(e);
            } else {
              tspan.attr('dy', 3);
              addTspan(e);
            }
          });

          const textElement = d3.selectAll(node);
          const textElementParentG = textElement.node().parentNode;

          const maxHeightTextElement = Number(textElement.attr('data-height'));
          const currentHeightTextElement = textElementParentG.getBBox().height;

          /**
           * FORMULA TO CENTRALIZE BASED IN HEIGHT
           * TOP of box is -50 (translate position y)
           * maxHeightTextElement     ----- -50
           * currentHeightTextElement -----  calculePositionToCentralize
           * calculePositionToCentralize = (currentHeightTextElement * -50) / maxHeightTextElement
           */
          const calculePositionToCentralize =
            (currentHeightTextElement * -50) / maxHeightTextElement;

          d3.select(textElementParentG).attr(
            'transform',
            (d) => `translate(0, ${calculePositionToCentralize.toFixed(0)})`,
          );
        }
      }

      if (tspanLength > rectWidth && line.length !== 1) {
        line.pop();
        tspan.text(line.join(' '));
        line = [word];
        tspan = addTspan(word);
      }
    }

    // Chunk function to split string
    function chunk(str: string, n: number) {
      let ret: string[] = [];
      let i;
      let len;

      for (i = 0, len = str.length; i < len; i += n) {
        let fff = str.substr(i, n);
        if (fff) {
          ret.push(fff);
        }
      }

      return ret;
    }

    // get unit kWh or €
    const valueAndUnit = words.pop();

    const changeValueDotToComma = valueAndUnit.split(' ')?.[0].split(',').join('.');

    const onlyUnit = valueAndUnit.split(' ')?.[1] || formatter.getCurrencySymbol(currency);

    let decreaseDecimalPlace = changeValueDotToComma.split('');
    const signal =
      decreaseDecimalPlace?.[0] === '+' || decreaseDecimalPlace?.[0] === '-'
        ? decreaseDecimalPlace[0]
        : '';

    decreaseDecimalPlace = decreaseDecimalPlace.join('').split('+').join('').split('-').join('');
    decreaseDecimalPlace = parseFloat(decreaseDecimalPlace).toFixed(2);

    let unit = addTspan(`${signal}${decreaseDecimalPlace} ${onlyUnit}`);
    // number of characters of the unit string
    const tspanUnitLength = unit.node()!.getComputedTextLength();

    const unitText = unit.text().split('_').reverse();
    const value = unitText[0].split(' ');

    let paddingTop = textSplitInArray?.length >= 2 ? 4 : 0;
    unit.attr(
      'dy',
      `${lineNumber * ChartEnergyBillsAndNetTradedConfig.fontSize + 3 + paddingTop}px`,
    );

    // consider arrow size and sign plus and minus to apply offset
    const offset = 20;
    // added unit into tspan considering the offset spacement
    if (tspanUnitLength + offset > rectWidth) {
      if (value.length > 1) {
        const lastItem = value.pop();

        unit.text(value[0]);
        paddingTop = 4;
        unit.attr(
          'dy',
          `${lineNumber * ChartEnergyBillsAndNetTradedConfig.fontSize + 3 + paddingTop}px`,
        );
        addTspan(lastItem, paddingTop);
      }
    }

    function addTspan(text, extraSpace = 0) {
      lineNumber += 1;

      return node
        .append('tspan')
        .attr('x', x)
        .attr('y', y)
        .attr(
          'dy',
          `${lineNumber * ChartEnergyBillsAndNetTradedConfig.fontSize + 3 + extraSpace}px`,
        )
        .text(text);
    }
  });
}

const getNodeText = ({ data }) => {
  if (data.scoreboardValue === 'energy') {
    const formatedValue = new Intl.NumberFormat('de-DE', {
      signDisplay: 'exceptZero',
    }).format(data.realValue);
    return `${data.title}_${formatedValue} kWh`;
  } else {
    const formatedValue = new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      signDisplay: 'exceptZero',
    }).format(data.realValue);
    return `${data.title}_${formatedValue}`;
  }
};

const getNodeFillColor = ({ value, scoreboardType }) => {
  switch (scoreboardType) {
    case 'Bought':
    case 'Total':
      if (value > 0) return ChartEnergyBillsAndNetTradedConfig.colors.red;
      if (value < 0) return ChartEnergyBillsAndNetTradedConfig.colors.green;
      return ChartEnergyBillsAndNetTradedConfig.colors.blue;
    case 'Sold':
      if (value > 0) return ChartEnergyBillsAndNetTradedConfig.colors.green;
      if (value < 0) return ChartEnergyBillsAndNetTradedConfig.colors.red;
      return ChartEnergyBillsAndNetTradedConfig.colors.blue;
  }
};

const createTreemapChart = ({
  container,
  data,
  width,
  height,
  scoreboardType,
  currency,
}: TCreateTreemapChart) => {
  // Create SVG element
  const svg = d3.select(container);
  svg.attr('width', width).attr('height', height).selectAll('g').remove();

  if (!data) return;

  // Create root node
  const root = d3
    .hierarchy(data)
    // @ts-ignore
    .sum((d) => d.chartValue)
    .sort(function (a, b) {
      // @ts-ignore
      return b.value - a.value;
    });

  // Create treemap layout
  const treemapRoot = d3.treemap().size([width, height]).padding(1).round(true)(root);

  // Create 'g' element nodes based on data
  const nodes = svg
    .selectAll('g')
    .data(treemapRoot.leaves())
    .join('g')
    .attr('transform', (d) => `translate(${d.x0},${d.y0})`);

  // Add treemap rects
  nodes
    .append('rect')
    .attr('width', (d) => d.x1 - d.x0)
    .attr('height', (d) => d.y1 - d.y0)
    .attr('rx', 3)
    .attr('fill', ({ data }) => {
      return getNodeFillColor({ value: data.realValue, scoreboardType });
    });

  const group = nodes.append('g').attr('data-coords', (d) => `${d.x1 - d.x0}-${d.y1 - d.y0}`);

  // Add text to rects
  group
    .append('text')
    .text(getNodeText)
    .attr('data-width', (d) => d.x1 - d.x0)
    .attr('data-height', (d) => d.y1 - d.y0)
    .attr('text-anchor', 'middle')
    .attr('font-size', `${ChartEnergyBillsAndNetTradedConfig.fontSize}px`)
    .attr('font-weight', 'normal')
    .attr('x', (d) => (d.x1 - d.x0) / 2)
    .attr('y', (d) => (d.y1 - d.y0) / 2)
    .attr('color', ChartEnergyBillsAndNetTradedConfig.textColor)
    .call(wrapText, currency);

  // Add arrow
  group
    .append('path')
    .attr('d', 'M3.00002 0L6 3H0L3.00002 0Z')
    .attr('fill', (d) =>
      // @ts-ignore
      d.data.realValue === 0 ? 'transparent' : ChartEnergyBillsAndNetTradedConfig.textColor,
    )
    .attr('transform', (d) => {
      // @ts-ignore
      const rotate = d.data.realValue < 0;
      let elem;

      group.each((g, index) => {
        if (`${g.x1 - g.x0}-${g.y1 - g.y0}` === `${d.x1 - d.x0}-${d.y1 - d.y0}`) {
          // @ts-ignore
          elem = group._groups[0][index];
        }
      });

      const valWidth = elem.children[0].children[
        elem.children[0].children.length - 1
      ].getComputedTextLength();

      return `translate(${(d.x1 - d.x0) / 2 - valWidth / 2 - 11 + (rotate ? 5 : 0)}, ${
        (d.y1 - d.y0) / 2 + 12 * (elem.children[0].children.length - 1) + (rotate ? 3.5 : 0)
      }) ${rotate ? 'rotate(180)' : ''}`;
    });
};

const scoreboardTypes = ['Bought', 'Sold', 'Total'];
const scoreboardValues: TBaseSwitchOptionIcon[] = [
  { value: 'energy', icon: 'lightning', info: 'Energy' },
  { value: 'money', icon: 'payment', info: 'Price' },
];

export const ChartEnergyBillsAndNetTraded: React.FC<TChartEnergyBillsAndNetTradedProps> = ({
  type,
  priceEnergyAreaBalance,
}) => {
  const expanded = useMemo(() => type === EChartEnergyBillsAndNetTradedType.Expanded, [type]);

  const [selectedTypeIndex, setSelectedTypeIndex] = useState(2);
  const [selectedScoreboardValueIndex, setSelectedScoreboardValueIndex] = useState(1);
  const sidebarExpanded = useSelector(selectSidebarExpanded);
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const { currency } = useSelector(selectSettingsData);

  const createChart = useCallback(() => {
    if (containerRef.current && svgRef.current) {
      createTreemapChart({
        container: svgRef.current,
        data:
          priceEnergyAreaBalance &&
          (getFormatedChartData(
            // Creating new object allow us to add new property in getFormatedChartData method
            { ...priceEnergyAreaBalance },
            scoreboardTypes[selectedTypeIndex],
            scoreboardValues[selectedScoreboardValueIndex].value as string,
          ) as any),
        width: containerRef.current?.clientWidth,
        height: 255,
        scoreboardType: scoreboardTypes[selectedTypeIndex],
        currency,
      });
    }
  }, [currency, priceEnergyAreaBalance, selectedScoreboardValueIndex, selectedTypeIndex]);

  useEffect(() => {
    createChart();
  }, [createChart, sidebarExpanded]);

  const filtersComponent = (
    <div
      className={classnames(s.filters, {
        [s.filtersCompact]: !expanded,
      })}>
      <BasePills
        className={s.chartDayPills}
        name="Time Range"
        theme="dark-gray"
        size="large"
        value={selectedTypeIndex}
        items={scoreboardTypes}
        onChange={({ value }) => setSelectedTypeIndex(value)}
      />
      <div>
        <BaseSwitch
          name="price-type"
          value={scoreboardValues[selectedScoreboardValueIndex].value}
          options={scoreboardValues}
          theme="gray-custom"
          variant="horizontal-small"
          onChange={({ value }) =>
            setSelectedScoreboardValueIndex(
              scoreboardValues.findIndex((item) => item.value === value),
            )
          }
        />
      </div>
    </div>
  );

  return (
    <ChartWrapper
      title="Bills and Traded Energy"
      headerComponent={expanded && filtersComponent}
      info={getChartTooltip(
        EChartTooltips.TOOLTIP_ENERGY_BILLS,
        formatter.getCurrencySymbol(currency),
      )}>
      <div className={s.container} ref={containerRef}>
        {!expanded && filtersComponent}
        <ChartDataLoadingWrapper loading={!priceEnergyAreaBalance}>
          <svg ref={svgRef} />
        </ChartDataLoadingWrapper>
      </div>
    </ChartWrapper>
  );
};
